'use client'

import ConfirmModal from '@/components/confirm-modal'
import AutoPaymentCard from '@/components/auto-payment/auto-payment-card'
import { SearchableSelect } from '@/components/forms/searchable-select'
import {
    getPayments,
    getPixQrCode,
    getSubscriptionPayments,
    Payment,
    SubscriptionPayment,
} from '@/lib/api/commerce/payments-api'
import { CompanySegment } from '@/lib/api/common/interfaces/company.interface'
import { PersonType } from '@/lib/api/common/interfaces/partner.interface'
import { updateCompany } from '@/lib/api/companies/companies-api'
import {
    DashboardData,
    getDashboardData,
} from '@/lib/api/dashboard/dashboard-api'
import { fetchPostalCode } from '@/lib/api/fetch-postal-code'
import { uploadOrganizationLogo } from '@/lib/api/organization/organization-api'
import { getPacks, Pack } from '@/lib/api/packs/packs-api'
import {
    getMyPartnerBillings,
    getPartner,
    MyPartnerBillingsResponse,
    updatePartner,
    uploadPartnerLogo,
} from '@/lib/api/partners/partners-api'
import {
    cancelRecurringPurchase,
    listRecurringPurchases,
    pauseRecurringPurchase,
    RecurringPurchase,
    resumeRecurringPurchase,
} from '@/lib/api/payment/payment-api'
import { changeUserPassword, updateUser } from '@/lib/api/users/users-api'
import { getPartnerPricing } from '@/lib/api/utils/settings-api'
import { SERVICE_LIST_ITEMS } from '@/lib/constants/service-list-items'
import { useOneSignal } from '@/lib/hooks/use-onesignal'
import {
    isFloatingNotificationsDisabled,
    setFloatingNotificationsDisabled,
} from '@/lib/hooks/use-notifications'
import { companyNameLabel, isIndividualCompany } from '@/lib/person-type'
import { getFileUrl } from '@/lib/utils/file-url'
import { useAppDispatch, useAuth } from '@/store/hooks'
import {
    logout,
    setAccessibleCompanies,
    setCredentials,
} from '@/store/slices/authSlice'
import {
    getMyLoginHistory,
    LoginHistoryItem,
} from '@/lib/api/events/events-api'
import { useCallback, useEffect, useRef, useState } from 'react'
import { BsChevronDown, BsChevronUp } from 'react-icons/bs'
import { FaWhatsapp } from 'react-icons/fa'
import {
    MdAccountBalance,
    MdAutorenew,
    MdBusiness,
    MdCalendarToday,
    MdCancel,
    MdCheckCircle,
    MdCloudUpload,
    MdContentCopy,
    MdEmail,
    MdError,
    MdImage,
    MdInfo,
    MdLanguage,
    MdLocationOn,
    MdLock,
    MdNotifications,
    MdNotificationsOff,
    MdPause,
    MdPayment,
    MdPerson,
    MdPhone,
    MdPlayArrow,
    MdReceipt,
    MdRefresh,
    MdSchedule,
    MdShoppingCart,
    MdSupportAgent,
    MdWarning,
} from 'react-icons/md'
import { handleDateInputPaste } from '@/lib/utils/date-input'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
    formatCep,
    formatCnpj,
    formatCpf,
    formatDateOnly,
    formatDateTime,
    formatPhone,
    formatPrice,
    getBillingStatusConfig,
    getSubscriptionStatusConfig,
} from './account-utils'
import NotificationsTab from './tabs/notifications-tab'
import FinancialTab from './tabs/financial-tab'
import AccountTab from './tabs/account-tab'
import CompanyTab from './tabs/company-tab'
import MyCompanyTab from './tabs/my-company-tab'
import PaymentsTab from './tabs/payments-tab'
import SubscriptionTab from './tabs/subscription-tab'
import PasswordTab from './tabs/password-tab'

// Descreve o dispositivo a partir do User-Agent (navegador + sistema).
export default function AccountPage() {
    const auth = useAuth()

    const { user, token, refreshToken, isCompany, isMaster, isPartner } = auth
    const location = useLocation()
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const [activeTab, setActiveTab] = useState(
        searchParams.get('tab') || 'account'
    )
    const canEditCompany = isMaster || isCompany

    // Conta de pessoa física: a "empresa" É o titular. Não há aba "Empresa" —
    // nome, documento, endereço e contato vivem na aba "Conta", e o backend
    // espelha o titular na Company a cada save.
    const isCompanyIndividual = isIndividualCompany(user?.company)

    // Debug: Log user data when component mounts or user changes
    useEffect(() => {
        if (user) {
            console.log('[AccountPage] User data:', user)
            console.log('[AccountPage] User company:', user.company)
            console.log('[AccountPage] User companyId:', user.companyId)
            if (user.company) {
                console.log(
                    '[AccountPage] Company keys:',
                    Object.keys(user.company)
                )
                console.log(
                    '[AccountPage] Company aboutUs:',
                    user.company.aboutUs
                )
                console.log(
                    '[AccountPage] Company website:',
                    user.company.website
                )
                console.log(
                    '[AccountPage] Company street:',
                    user.company.street
                )
            }
        }
    }, [user])

    // O link "#assinatura" abre direto na aba; o carregamento dos dados e da
    // propria aba, que busca ao montar.
    useEffect(() => {
        if (location.hash === '#assinatura' && !isPartner) {
            setActiveTab('financial')
        }
    }, [location.hash, isPartner])

    // `?tab=company` guardado nos favoritos (ou vindo de um link antigo) abriria
    // uma aba que nao existe para pessoa fisica, deixando o card vazio.
    useEffect(() => {
        if (isCompanyIndividual && activeTab === 'company') {
            setActiveTab('account')
        }
    }, [isCompanyIndividual, activeTab])
    const [loading, setLoading] = useState(false)
    const [loadingCompany, setLoadingCompany] = useState(false)
    const [loadingCep, setLoadingCep] = useState(false)
    const [recurringPurchases, setRecurringPurchases] = useState<
        RecurringPurchase[]
    >([])
    const [loadingRecurring, setLoadingRecurring] = useState(false)
    const [recurringLoaded, setRecurringLoaded] = useState(false)
    const [recurringConfirm, setRecurringConfirm] = useState<{
        id: number
        action: 'cancel' | 'pause' | 'resume'
    } | null>(null)
    const [recurringActionLoading, setRecurringActionLoading] = useState(false)
    const [uploadingLogo, setUploadingLogo] = useState(false)
    const [logoPreview, setLogoPreview] = useState<string | null>(null)
    const logoInputRef = useRef<HTMLInputElement>(null)

    const isHealthSegment =
        (user?.company as any)?.segment === CompanySegment.HEALTH

    const dispatch = useAppDispatch()

    return (
        <>
            <div className="flex flex-col gap-6 animate-fade-in">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary to-secondary/70 flex items-center justify-center shadow-lg">
                        <MdPerson className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-base-content">
                            Minha Conta
                        </h1>
                        <p className="text-sm text-base-content/60">
                            Gerencie suas informações pessoais e da empresa
                        </p>
                    </div>
                </div>

                <div className="tabs tabs-boxed bg-base-200/50 p-1 rounded-xl">
                    <button
                        className={`tab flex-1 gap-2 ${
                            activeTab === 'account'
                                ? 'tab-active bg-secondary text-primary-content'
                                : ''
                        }`}
                        onClick={() => setActiveTab('account')}
                    >
                        <MdPerson className="text-lg" />
                        <span className="hidden sm:inline">Conta</span>
                    </button>

                    {isPartner && (
                        <button
                            className={`tab flex-1 gap-2 ${
                                activeTab === 'my-company'
                                    ? 'tab-active bg-secondary text-primary-content'
                                    : ''
                            }`}
                            onClick={() => setActiveTab('my-company')}
                        >
                            <MdBusiness className="text-lg" />
                            <span className="hidden sm:inline">
                                Minha Empresa
                            </span>
                        </button>
                    )}
                    {/*
                        Conta de pessoa física não tem aba "Empresa": não existe
                        empresa — a Company É o titular. Os dados dela (nome,
                        documento, endereço, contato) são os da aba "Conta", e o
                        backend espelha o titular na Company a cada save. Ver
                        docs/person-type-pf-pj.md.
                    */}
                    {canEditCompany && !isCompanyIndividual && (
                        <button
                            className={`tab flex-1 gap-2 ${
                                activeTab === 'company'
                                    ? 'tab-active bg-secondary text-primary-content'
                                    : ''
                            }`}
                            onClick={() => setActiveTab('company')}
                        >
                            <MdBusiness className="text-lg" />
                            <span className="hidden sm:inline">Empresa</span>
                        </button>
                    )}
                    <button
                        className={`tab flex-1 gap-2 ${
                            activeTab === 'password'
                                ? 'tab-active bg-secondary text-primary-content'
                                : ''
                        }`}
                        onClick={() => setActiveTab('password')}
                    >
                        <MdLock className="text-lg" />
                        <span className="hidden sm:inline">Senha</span>
                    </button>
                    <button
                        className={`tab flex-1 gap-2 ${
                            activeTab === 'notifications'
                                ? 'tab-active bg-secondary text-primary-content'
                                : ''
                        }`}
                        onClick={() => setActiveTab('notifications')}
                    >
                        <MdNotifications className="text-lg" />
                        <span className="hidden sm:inline">Notificações</span>
                    </button>
                    {canEditCompany && (
                        <button
                            className={`tab flex-1 gap-2 ${
                                activeTab === 'financial'
                                    ? 'tab-active bg-secondary text-primary-content'
                                    : ''
                            }`}
                            onClick={() => setActiveTab('financial')}
                        >
                            <MdAccountBalance className="text-lg" />
                            <span className="hidden sm:inline">Assinatura</span>
                        </button>
                    )}
                    {/*canEditCompany && (user?.company?.organization?.subscription?.plan?.customPrice ?? user?.company?.organization?.subscription?.plan?.price ?? 0) > 0 && (
                        <button
                            className={`tab flex-1 gap-2 ${activeTab === 'payments'
                                ? 'tab-active bg-secondary text-primary-content'
                                : ''
                                }`}
                            onClick={() => setActiveTab('payments')}
                        >
                            <MdPayment className="text-lg" />
                            <span className="hidden sm:inline">Pagamentos</span>
                        </button>
                    )*/}
                    {isPartner && (
                        <button
                            className={`tab flex-1 gap-2 ${
                                activeTab === 'my-subscription'
                                    ? 'tab-active bg-secondary text-primary-content'
                                    : ''
                            }`}
                            onClick={() => setActiveTab('my-subscription')}
                        >
                            <MdAccountBalance className="text-lg" />
                            <span className="hidden sm:inline">
                                Minha Assinatura
                            </span>
                        </button>
                    )}
                </div>

                <div className="card bg-base-100 shadow-xl border border-base-300/50">
                    <div className="card-body p-6">
                        {activeTab === 'account' && (
                            <AccountTab
                                onSaved={() => {
                                    // PF não tem aba "Empresa" para onde mandar o
                                    // usuário — os dados dela já foram salvos junto,
                                    // então ele fica onde está.
                                    if (isCompanyIndividual) return

                                    if (canEditCompany) {
                                        setActiveTab('company')
                                        toast.info(
                                            'Agora altere dados da empresa se precisar'
                                        )
                                    } else if (isPartner) {
                                        setActiveTab('my-company')
                                    }
                                }}
                            />
                        )}

                        {activeTab === 'my-company' && isPartner && (
                            <MyCompanyTab />
                        )}

                        {activeTab === 'company' && !isCompanyIndividual && (
                            <CompanyTab />
                        )}

                        {activeTab === 'password' && <PasswordTab />}

                        {activeTab === 'notifications' && <NotificationsTab />}

                        {activeTab === 'financial' && !isPartner && (
                            <FinancialTab />
                        )}

                        {activeTab === 'payments' && <PaymentsTab />}

                        {activeTab === 'my-subscription' && (
                            <SubscriptionTab />
                        )}
                    </div>
                </div>
            </div>

        </>
    )
}
